import { useTranslation } from 'next-i18next'
import { useEffect, useRef } from 'react'

import type { Language, SupportedMarketKey } from '@nxt-insurance/constants'

import { useCoupon } from '@/hooks/useCoupon'
import { isAirpodsCampaignFlow, isAirpodsCoupon } from '@/utils/airpodsCampaign'

import type { FlowStepPageConfig } from './types'

const isIgnoredCode = (
  code: string | null | undefined,
  flowName: string,
  isAuthenticated: boolean
) => isAirpodsCoupon(code) && (!isAirpodsCampaignFlow(flowName) || isAuthenticated)

export const useFlowCoupon = ({
  config,
  sessionFlowValues,
  isAuthenticated,
  urlCoupon,
  hasFlowSession,
  language,
  market,
}: {
  config: FlowStepPageConfig
  sessionFlowValues: Record<string, unknown> | undefined
  isAuthenticated: boolean
  urlCoupon: unknown
  hasFlowSession: boolean
  language: Language
  market: SupportedMarketKey
}) => {
  const { t } = useTranslation()
  const coupon = useCoupon({
    sessionFlowValues,
    urlCode: typeof urlCoupon === 'string' ? urlCoupon : undefined,
    hasFlowSession,
    getErrorMessage: (reason) => t(`sharedFlows_common:orderSummary.checkout.codeErrors.${reason}`),
  })
  const couponMutation = config.router.validateCode.useMutation({
    onSuccess: (data) => coupon.applyResult(data),
  })
  const autoValidatedCode = useRef<string | null>(null)
  const { mutate: validateCodeFromUrl } = config.router.validateCode.useMutation({
    onSuccess: (data) => coupon.applyAutoResult(data),
    onError: () => coupon.applyAutoResult(null),
    onSettled: () => {
      autoValidatedCode.current = null
    },
  })

  const codeToValidate = isIgnoredCode(coupon.codeToValidate, config.flowName, isAuthenticated)
    ? null
    : coupon.codeToValidate
  useEffect(() => {
    if (!codeToValidate || autoValidatedCode.current === codeToValidate) {
      return
    }
    autoValidatedCode.current = codeToValidate
    validateCodeFromUrl({ code: codeToValidate, language, market })
  }, [codeToValidate, language, market, validateCodeFromUrl])

  const appliedCoupon = isIgnoredCode(coupon.appliedCoupon?.code, config.flowName, isAuthenticated)
    ? null
    : coupon.appliedCoupon

  return {
    coupon,
    couponMutation,
    codeToValidate,
    appliedCoupon,
    couponPending: couponMutation.isPending || codeToValidate !== null,
  }
}
